import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    editDirectPromotionAsync,
    saveDirectPromotionAsync,
} from '../../../../data-layer/direct/actions';
import { selectDirectCampaignSaving } from '../../../../data-layer/direct/selectors';
import {
    DirectBudgetType,
    DirectCampaignUI,
    DirectPayForType,
    DirectSpendLimitType,
    DirectStrategyType,
} from '../../../../data-layer/direct/types';
import { showNotificationAsync } from '../../../../data-layer/notifications/actions';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';
import { ContentPromotionPostsValue } from '../../../../ui/business/ContentPromotionPosts/types';
import { isBlogger } from '../../../../utils/accessService';
import { useExperimentFlag } from '../../../../utils/useExperimentFlag';
import { useCurrentUser } from '../../../users/useCurrentUser';
import { MIN_WEEKLY_LIMIT_VALUE } from '../constants';
import { useIsAuthorized } from './useIsAuthorized';

interface PromotionParams {
    date: string;
    weeklyLimit?: number;
    selectedCounters: string[];
    selectedGoals: Record<string, number | undefined>;
    disabledPosts: string[];
    postsValue?: ContentPromotionPostsValue;
    selectedRegions: Record<string, string>;
    selectedLogin?: string;
    utmParams?: string;
    strategyType: DirectStrategyType;
    payFor: DirectPayForType;
    budgetType: DirectBudgetType;
    spendLimitType: DirectSpendLimitType;
    spendLimitAmount?: number;
}

export const useSavePromotion = (params: PromotionParams, company?: DirectCampaignUI) => {
    const {
        date,
        weeklyLimit = 0,
        selectedCounters,
        selectedGoals,
        disabledPosts,
        postsValue,
        selectedRegions,
        selectedLogin,
        utmParams,
        strategyType,
        payFor,
        budgetType,
        spendLimitType,
        spendLimitAmount,
    } = params;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUser = useCurrentUser();
    const profile = currentUser?.profile;
    const businessId = profile?.id;
    const publicId = profile?.publicId;
    const accountPrefix = isBlogger(currentUser?.role) ? 'profile' : 'businesses';
    const redirectUrl = `/${accountPrefix}/${publicId}/management`;
    const isAuthorized = useIsAuthorized();
    const companyId = company?.id;
    const isSaving = useSelector(selectDirectCampaignSaving);

    const isStrategyEnabled = Number(useExperimentFlag('content_promotion_strategy')) === 1;

    const promotionData = useMemo(
        () =>
            businessId && {
                businessId,
                campaign: {
                    startDate: date,
                    weeklyLimit,
                    regions: selectedRegions,
                    counters: selectedCounters,
                    goals: selectedGoals,
                    login: selectedLogin,
                    utmParams,
                    strategyType: isStrategyEnabled ? strategyType : undefined,
                    payFor: isStrategyEnabled ? payFor : undefined,
                    budgetType: isStrategyEnabled ? budgetType : undefined,
                    spendLimitType: isStrategyEnabled ? spendLimitType : undefined,
                    spendLimitAmount: isStrategyEnabled ? spendLimitAmount : undefined,
                },
                feedSettings: {
                    posts: disabledPosts,
                    useAllPosts:
                        postsValue === ContentPromotionPostsValue.ALL ||
                        postsValue === ContentPromotionPostsValue.CHOICE,
                    freshnessDays: postsValue === ContentPromotionPostsValue.NEW ? 30 : undefined,
                },
            },
        [
            businessId,
            date,
            weeklyLimit,
            selectedRegions,
            selectedCounters,
            selectedGoals,
            selectedLogin,
            utmParams,
            isStrategyEnabled,
            strategyType,
            payFor,
            budgetType,
            spendLimitType,
            spendLimitAmount,
            disabledPosts,
            postsValue,
        ],
    );

    const checkWeeklyLimit = useCallback(() => {
        const isValid = weeklyLimit >= MIN_WEEKLY_LIMIT_VALUE;

        if (!isValid) {
            dispatch(
                showNotificationAsync(
                    `Недельный бюджет должен быть не меньше ${MIN_WEEKLY_LIMIT_VALUE}₽`,
                ),
            );
        }

        return isValid;
    }, [weeklyLimit, dispatch]);

    const onStartPromotion = useCallback(() => {
        if (!isAuthorized || !promotionData || !checkWeeklyLimit()) {
            return;
        }

        dispatch(saveDirectPromotionAsync(promotionData, redirectUrl, navigate));
    }, [promotionData, redirectUrl, isAuthorized, checkWeeklyLimit, dispatch, navigate]);

    const onSavePromotion = useCallback(() => {
        if (!companyId || !promotionData || !checkWeeklyLimit()) {
            return;
        }

        dispatch(editDirectPromotionAsync(promotionData, String(companyId), redirectUrl, navigate));
    }, [promotionData, redirectUrl, companyId, checkWeeklyLimit, dispatch, navigate]);

    return { isSaving, onStartPromotion, onSavePromotion };
};
